import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { tiposArquivosLabel } from '../../../Enum/tipoArquivos';
import './index.css'; // Você pode criar essa classe para destacar coluna ordenada

function ListaArquivos({ arquivos, onRenomear, onDeletar }) {
    const [editArquivo, setEditArquivo] = useState(null);
    const [novoNome, setNovoNome] = useState("");
    const [ordem, setOrdem] = useState({ coluna: "", direcao: "asc" });
    const [paginaAtual, setPaginaAtual] = useState(1);
    const porPagina = 10;

    const abrirEditar = (arquivo) => {
        setEditArquivo(arquivo);
        setNovoNome(arquivo.nomeArquivo);
    };

    const salvarNome = async () => {
        if (!novoNome.trim()) {
            await Swal.fire({
                icon: "warning",
                title: "Nome inválido",
                text: "Nome não pode ser vazio.",
            });
            return;
        }

        onRenomear(editArquivo.arquivoID, novoNome.trim());

        await Swal.fire({
            icon: "success",
            title: "Arquivo renomeado",
            timer: 1200,
            showConfirmButton: false,
        });

        setEditArquivo(null);
    };

    const confirmarExclusao = async (arquivoID) => {
        const confirm = await Swal.fire({
            title: "Excluir arquivo?",
            text: "Esta ação não poderá ser desfeita.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, excluir",
            confirmButtonColor: "red",
            cancelButtonText: "Cancelar",
        });

        if (confirm.isConfirmed) {
            onDeletar(arquivoID);
            await Swal.fire({
                icon: "success",
                title: "Arquivo excluído",
                timer: 1200,
                showConfirmButton: false,
            });
        }
    };

    const alternarOrdenacao = (coluna) => {
        setOrdem((prev) => ({
            coluna,
            direcao: prev.coluna === coluna && prev.direcao === "asc" ? "desc" : "asc",
        }));
        setPaginaAtual(1);
    };

    const arquivosOrdenados = useMemo(() => {
        if (!ordem.coluna) return arquivos;

        return [...arquivos].sort((a, b) => {
            const getVal = (item) => {
                if (ordem.coluna === "tipo") {
                    return (tiposArquivosLabel[item.tipo] || "Outro").toLowerCase();
                }
                if (ordem.coluna === "created") {
                    return new Date(item.created);
                }
                return item[ordem.coluna]?.toLowerCase?.() || "";
            };

            const aVal = getVal(a);
            const bVal = getVal(b);

            if (aVal < bVal) return ordem.direcao === "asc" ? -1 : 1;
            if (aVal > bVal) return ordem.direcao === "asc" ? 1 : -1;
            return 0;
        });
    }, [arquivos, ordem]);

    const totalPaginas = Math.ceil(arquivosOrdenados.length / porPagina);
    const arquivosPaginados = arquivosOrdenados.slice(
        (paginaAtual - 1) * porPagina,
        paginaAtual * porPagina
    );

    const gerarPaginas = () => {
        const paginas = [];
        if (totalPaginas <= 7) {
            for (let i = 1; i <= totalPaginas; i++) paginas.push(i);
        } else {
            paginas.push(1);
            if (paginaAtual > 3) paginas.push("...");
            for (
                let i = Math.max(2, paginaAtual - 1);
                i <= Math.min(totalPaginas - 1, paginaAtual + 1);
                i++
            ) {
                paginas.push(i);
            }
            if (paginaAtual < totalPaginas - 2) paginas.push("...");
            paginas.push(totalPaginas);
        }
        return paginas;
    };

    const getIcon = (coluna) => {
        if (ordem.coluna !== coluna) return <i className="fas fa-sort text-muted"></i>;
        return ordem.direcao === "asc" ? (
            <i className="fas fa-sort-up"></i>
        ) : (
            <i className="fas fa-sort-down"></i>
        );
    };

    return (
        <div>
            {arquivos.length === 0 ? (
                <p>Nenhum arquivo encontrado.</p>
            ) : (
                <>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th onClick={() => alternarOrdenacao("nomeArquivo")} className="sortable">
                                    Nome do Arquivo {getIcon("nomeArquivo")}
                                </th>
                                <th onClick={() => alternarOrdenacao("tipo")} className="sortable">
                                    Tipo {getIcon("tipo")}
                                </th>
                                <th onClick={() => alternarOrdenacao("created")} className="sortable">
                                    Data Upload {getIcon("created")}
                                </th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {arquivosPaginados.map((arq) => (
                                <tr key={arq.arquivoID}>
                                    <td className={ordem.coluna === "nomeArquivo" ? "bg-light" : ""}>
                                        {arq.nomeArquivo}
                                    </td>
                                    <td className={ordem.coluna === "tipo" ? "bg-light" : ""}>
                                        {tiposArquivosLabel[arq.tipo] || "Desconhecido"}
                                    </td>
                                    <td className={ordem.coluna === "created" ? "bg-light" : ""}>
                                        {new Date(arq.created).toLocaleString()}
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-outline-primary btn-sm me-2"
                                            onClick={() => abrirEditar(arq)}
                                            title="Renomear"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => confirmarExclusao(arq.arquivoID)}
                                            title="Excluir"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Paginação compacta */}
                    {totalPaginas > 1 && (
                        <nav className="d-flex justify-content-center mt-3">
                            <ul className="pagination">
                                {gerarPaginas().map((p, i) => (
                                    <li
                                        key={i}
                                        className={`page-item ${p === paginaAtual ? "active" : ""} ${p === "..." ? "disabled" : ""}`}
                                        onClick={() => typeof p === "number" && setPaginaAtual(p)}
                                    >
                                        <button className="page-link">{p}</button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </>
            )}

            {/* Modal de Renomear */}
            {editArquivo && (
                <div
                    className="modal d-block"
                    tabIndex={-1}
                    role="dialog"
                    onClick={() => setEditArquivo(null)}
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div
                        className="modal-dialog"
                        role="document"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Renomear arquivo</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setEditArquivo(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={novoNome}
                                    maxLength={255}
                                    style={{ paddingTop: 10, paddingBottom: 10, fontSize: 14 }}
                                    onChange={(e) => setNovoNome(e.target.value)}
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setEditArquivo(null)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={salvarNome}
                                >
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ListaArquivos;